import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { cloneDeep, isEqual } from 'lodash'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { showToast, toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import { ContactsApi } from 'entities/Contacts'
import { type Contact } from 'entities/Contacts/model/Contact'
import { ContactOptIn } from 'entities/Contacts/model/ContactOptIn'
import { type IParamsContactsOptOut } from 'entities/Contacts/api/types'
import { type IResponseFilterSegment } from 'entities/Segment'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ContactsOptInModalActions } from 'widgets/ContactsOptInModal/ui/ContactsOptInModalActions'
import { ContactsOptInModalContent } from 'widgets/ContactsOptInModal/ui/ContactsOptInModalContent'

export type IContactsOptInModalLocalConfigOptionsStore = {
  reset?: () => void
  isBulkAll: boolean
  filtersList?: IResponseFilterSegment[]
  search?: string
  successHandler?: (opt_outs: IParamsContactsOptOut['opt_outs']) => void
}

export type IContactsOptInModalLocalConfigStore = {
  contacts: Contact[]
  id: string
  deleteModal: (id: string) => void
  options?: IContactsOptInModalLocalConfigOptionsStore
}

export class ContactsOptInModalLocalStore {
  private _idModal = 'contacts_opt_in_modal'
  private _contacts: Contact[] = []
  private _itemsOptInMap: Map<number | string, ContactOptIn> = new Map()
  private _id = ''
  private _deleteModal: (id: string) => void
  private _options: IContactsOptInModalLocalConfigOptionsStore | null = null
  private _cacheParams: IParamsContactsOptOut | null = null

  constructor(config: IContactsOptInModalLocalConfigStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._contacts = config.contacts
    this._id = config.id
    this._deleteModal = config.deleteModal
    this._options = config.options || null

    this.initItems()

    modalStore.addModal({
      id: this._idModal,
      title: 'Opt-in status',
      width: 360,
      ModalContentProps: {
        store: this,
      },
      ModalActions: ContactsOptInModalActions,
      ModalContent: ContactsOptInModalContent,
      paddingContent: '0 22px 14px 24px',
      onClose: this.closeModal,
    })
  }

  reset = () => {
    this._contacts = []
    this._itemsOptInMap.clear()
    this._deleteModal(this._id)
  }

  initItems = () => {
    this._contacts.forEach((contact) => {
      const items = contact.optItems

      items.forEach((item) => {
        this._itemsOptInMap.set(item.id, new ContactOptIn(item, this._entities))
      })
    })

    this._cacheParams = cloneDeep(this.params)
  }

  closeModal = () => {
    modalStore.removeModal(this._idModal)
    this._deleteModal(this._id)
  }

  handleSaveUndo = () => {
    this.closeModal()

    const nanoId = nanoid()
    const id = setTimeout(() => {
      this.handleSave(nanoId)
    }, 5000)

    showToast({
      id: nanoId,
      type: 'success',
      title: 'Contact opt-in status changed',
      action: {
        text: 'Undo',
        onAction: () => {
          clearTimeout(id)
          this.reset()
          toastStore.remove(nanoId)
        },
      },
    })
  }

  handleSave = async (id?: string) => {
    if (!this.params) return

    if (id) toastStore.remove(id)

    try {
      await ContactsApi.updateContactsOptOut(this.params)

      this._options?.successHandler?.(this.params.opt_outs)
    } catch (e) {
      logger.error(e)
    } finally {
      this.reset()
      this._options?.reset?.()
    }
  }

  get params(): IParamsContactsOptOut | null {
    if (!this._contacts.length) return null

    const contactsIds = this._contacts.map((item) => item.id)
    const local = this._itemsOptInMap.get('local')
    const shortcodes = this.items.reduce((state, item) => {
      if (item.id !== 'local') {
        return {
          ...state,
          [item.id]: item.value,
        }
      }

      return state
    }, {})

    if (!local) return null

    return {
      contacts: contactsIds,
      opt_outs: {
        toll_free: local.value,
        shortcodes: shortcodes,
      },
      type: 'opt-out',
      bulk_all: this._options?.isBulkAll || false,
      filtersList: this._options?.filtersList,
      search: this._options?.search,
    }
  }

  get items() {
    return Array.from(this._itemsOptInMap.values())
  }

  get disabled() {
    if (this._contacts.length !== 1) return false

    return isEqual(this._cacheParams, this.params)
  }
}
